import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Pagination } from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useNewsletterSubscriptions, SubscriptionStatus } from "@/hooks/useNewsletterSubscriptions";
import { format } from "date-fns";
import { 
  Search, 
  Plus, 
  Trash2, 
  MoreHorizontal, 
  Filter, 
  Download, 
  Upload, 
  CheckSquare,
  ChevronUp,
  ChevronDown,
  SlidersHorizontal
} from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { usePermissions } from "@/hooks/usePermissions";
import { supabase } from "@/integrations/supabase/client";

export function SubscribersList() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<'subscribed_at' | 'email' | 'last_activity_at'>('subscribed_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSubscriber, setNewSubscriber] = useState({ email: '', name: '' });
  const { hasPermission } = usePermissions();
  const [selectedSubscribers, setSelectedSubscribers] = useState<string[]>([]);
  const [bulkActionDialog, setBulkActionDialog] = useState<{open: boolean, action: string}>({
    open: false,
    action: ''
  });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState<File | null>(null);
  const [advancedFilters, setAdvancedFilters] = useState(false);
  const [dateRangeFilter, setDateRangeFilter] = useState({
    from: '',
    to: ''
  });
  
  const { data, isLoading, refetch } = useNewsletterSubscriptions({
    page,
    search,
    status: statusFilter,
    sortBy,
    sortOrder,
    dateFrom: dateRangeFilter.from || undefined,
    dateTo: dateRangeFilter.to || undefined
  });
  
  const subscribers = data?.data || [];
  const pagination = data?.pagination || { page: 1, pageSize: 10, total: 0, totalPages: 1 };
  
  const handleStatusChange = (value: string) => {
    setStatusFilter(value as SubscriptionStatus | 'all');
    setPage(1);
  };
  
  const handleSort = (field: 'subscribed_at' | 'email' | 'last_activity_at') => {
    if (field === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };
  
  const handleDelete = async (id: string) => {
    if (!hasPermission('manage:newsletters')) {
      toast.error("You don't have permission to delete subscribers");
      return;
    }
    
    if (confirm("Are you sure you want to delete this subscriber?")) {
      try {
        const { error } = await supabase
          .from('newsletter_subscriptions')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        
        toast.success("Subscriber deleted successfully");
        refetch();
      } catch (error) {
        console.error("Error deleting subscriber:", error);
        toast.error("Failed to delete subscriber");
      }
    }
  };
  
  const handleAddSubscriber = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newSubscriber.email) {
      toast.error("Email is required");
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('newsletter_subscriptions')
        .insert([
          { 
            email: newSubscriber.email, 
            name: newSubscriber.name || null,
            status: 'active',
            subscribed_at: new Date().toISOString()
          }
        ])
        .select();
        
      if (error) throw error;
      
      toast.success("Subscriber added successfully");
      setNewSubscriber({ email: '', name: '' });
      setShowAddForm(false);
      refetch();
    } catch (error: any) {
      console.error("Error adding subscriber:", error);
      
      if (error.code === '23505') {
        toast.error("Email already exists in the subscription list");
      } else {
        toast.error("Failed to add subscriber");
      }
    }
  };
  
  const getStatusBadge = (status: SubscriptionStatus) => {
    switch(status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>;
      case 'unsubscribed':
        return <Badge variant="secondary" className="bg-slate-100 text-slate-800 hover:bg-slate-100">Unsubscribed</Badge>;
      case 'bounced':
        return <Badge variant="destructive" className="bg-red-100 text-red-800 hover:bg-red-100">Bounced</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const handleBulkAction = async (action: string) => {
    if (!hasPermission('manage:newsletters')) {
      toast.error("You don't have permission to perform bulk actions");
      return;
    }

    if (selectedSubscribers.length === 0) {
      toast.error("No subscribers selected");
      return;
    }

    try {
      switch (action) {
        case 'delete':
          const { error: deleteError } = await supabase
            .from('newsletter_subscriptions')
            .delete()
            .in('id', selectedSubscribers);
            
          if (deleteError) throw deleteError;
          toast.success(`${selectedSubscribers.length} subscribers deleted successfully`);
          break;
          
        case 'activate':
          const { error: activateError } = await supabase
            .from('newsletter_subscriptions')
            .update({ status: 'active' })
            .in('id', selectedSubscribers);
            
          if (activateError) throw activateError;
          toast.success(`${selectedSubscribers.length} subscribers marked as active`);
          break;
          
        case 'unsubscribe':
          const { error: unsubscribeError } = await supabase
            .from('newsletter_subscriptions')
            .update({ status: 'unsubscribed' })
            .in('id', selectedSubscribers);
            
          if (unsubscribeError) throw unsubscribeError;
          toast.success(`${selectedSubscribers.length} subscribers marked as unsubscribed`);
          break;
      }
      
      // Reset selected subscribers and close the dialog
      setSelectedSubscribers([]);
      setBulkActionDialog({ open: false, action: '' });
      refetch();
    } catch (error) {
      console.error(`Error performing bulk action ${action}:`, error);
      toast.error(`Failed to perform bulk action: ${error}`);
    }
  };
  
  const handleToggleSelectAll = () => {
    if (selectedSubscribers.length === subscribers.length) {
      setSelectedSubscribers([]);
    } else {
      setSelectedSubscribers(subscribers.map(s => s.id));
    }
  };
  
  const handleToggleSelectSubscriber = (id: string) => {
    if (selectedSubscribers.includes(id)) {
      setSelectedSubscribers(selectedSubscribers.filter(s => s !== id));
    } else {
      setSelectedSubscribers([...selectedSubscribers, id]);
    }
  };
  
  const exportSubscribers = () => {
    if (!hasPermission('manage:newsletters')) {
      toast.error("You don't have permission to export subscribers");
      return;
    }
    
    try {
      const dataToExport = selectedSubscribers.length > 0
        ? subscribers.filter(s => selectedSubscribers.includes(s.id))
        : subscribers;
        
      const csv = [
        ['Email', 'Name', 'Status', 'Subscribed At', 'Last Activity'].join(','),
        ...dataToExport.map(s => [
          s.email,
          s.name || '',
          s.status,
          s.subscribed_at || '',
          s.last_activity_at || ''
        ].map(field => `"${field}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`${dataToExport.length} subscribers exported successfully`);
    } catch (error) {
      console.error("Error exporting subscribers:", error);
      toast.error("Failed to export subscribers");
    }
  };
  
  const handleImportSubscribers = async () => {
    if (!importData) {
      toast.error("No file selected");
      return;
    }
    
    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        const csvText = e.target?.result as string;
        const lines = csvText.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const emailIndex = headers.indexOf('email');
        const nameIndex = headers.findIndex(h => h === 'name' || h === 'fullname' || h === 'full name');
        
        if (emailIndex === -1) {
          toast.error("CSV must contain an 'email' column");
          return;
        }
        
        const subscribers = [];
        let successCount = 0;
        let errorCount = 0;
        
        // Skip the header row
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          
          const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const email = values[emailIndex];
          const name = nameIndex !== -1 ? values[nameIndex] : null;
          
          if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errorCount++;
            continue;
          }
          
          subscribers.push({
            email,
            name,
            status: 'active',
            subscribed_at: new Date().toISOString()
          });
          
          // Insert in batches of 10 to avoid hitting limits
          if (subscribers.length === 10) {
            try {
              const { error } = await supabase
                .from('newsletter_subscriptions')
                .upsert(subscribers, { 
                  onConflict: 'email',
                  ignoreDuplicates: true
                });
                
              if (!error) successCount += subscribers.length;
              else errorCount += subscribers.length;
            } catch (err) {
              console.error("Error in batch insert:", err);
              errorCount += subscribers.length;
            }
            
            subscribers.length = 0;
          }
        }
        
        // Insert any remaining subscribers
        if (subscribers.length > 0) {
          try {
            const { error } = await supabase
              .from('newsletter_subscriptions')
              .upsert(subscribers, { 
                onConflict: 'email',
                ignoreDuplicates: true
              });
              
            if (!error) successCount += subscribers.length;
            else errorCount += subscribers.length;
          } catch (err) {
            console.error("Error in final batch insert:", err);
            errorCount += subscribers.length;
          }
        }
        
        toast.success(`Imported ${successCount} subscribers successfully${errorCount ? `. ${errorCount} failed.` : ''}`);
        setImportDialogOpen(false);
        setImportData(null);
        refetch();
      };
      
      reader.readAsText(importData);
    } catch (error) {
      console.error("Error importing subscribers:", error);
      toast.error("Failed to import subscribers");
    }
  };
  
  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setSortBy("subscribed_at");
    setSortOrder("desc");
    setDateRangeFilter({ from: '', to: '' });
    setPage(1);
  };
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mb-4">
        <div className="w-full relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="search"
            placeholder="Search subscribers..."
            className="pl-9 border-slate-200"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && refetch()}
          />
        </div>
        
        <div className="flex flex-wrap gap-2 justify-end">
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[140px] bg-white border-slate-200">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
              <SelectItem value="bounced">Bounced</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            size="icon"
            className="border-slate-200"
            onClick={() => setAdvancedFilters(!advancedFilters)}
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
          
          <Button 
            variant="outline"
            className="border-slate-200"
            onClick={() => refetch()}
          >
            Search
          </Button>
        </div>
      </div>
      
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex flex-wrap gap-2">
          {selectedSubscribers.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-slate-200">
                  <CheckSquare className="h-4 w-4 mr-2" />
                  {selectedSubscribers.length} Selected
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Bulk Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setBulkActionDialog({ open: true, action: 'activate' })}>
                  Mark as Active
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setBulkActionDialog({ open: true, action: 'unsubscribe' })}>
                  Mark as Unsubscribed
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={exportSubscribers}>
                  Export Selected
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => setBulkActionDialog({ open: true, action: 'delete' })}
                  className="text-destructive"
                >
                  Delete Selected
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-slate-200">
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Import Subscribers</DialogTitle>
                <DialogDescription>
                  Upload a CSV file with subscriber data. The file must include an 'email' column.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportData(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum file size: 5MB. Format: CSV
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleImportSubscribers} disabled={!importData}>
                  Import
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Button variant="outline" className="border-slate-200" onClick={exportSubscribers}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          
          <Button 
            className="bg-slate-900 hover:bg-slate-800"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Subscriber
          </Button>
        </div>
      </div>
      
      {advancedFilters && (
        <Card className="mb-4 border-slate-200">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="date-from" className="text-sm font-medium">
                  Subscribed From
                </label>
                <Input
                  id="date-from"
                  type="date"
                  className="border-slate-200"
                  value={dateRangeFilter.from}
                  onChange={(e) => setDateRangeFilter({...dateRangeFilter, from: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="date-to" className="text-sm font-medium">
                  Subscribed To
                </label>
                <Input
                  id="date-to"
                  type="date"
                  className="border-slate-200"
                  value={dateRangeFilter.to}
                  onChange={(e) => setDateRangeFilter({...dateRangeFilter, to: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={resetFilters} className="mr-2 border-slate-200">
                Reset Filters
              </Button>
              <Button type="button" onClick={() => refetch()}>
                Apply Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {showAddForm && (
        <Card className="mb-4 border-slate-200">
          <CardHeader>
            <CardTitle>Add New Subscriber</CardTitle>
            <CardDescription>Add a new subscriber to your newsletter</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddSubscriber} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">Email</label>
                  <Input
                    id="email"
                    type="email"
                    required
                    className="border-slate-200"
                    value={newSubscriber.email}
                    onChange={(e) => setNewSubscriber({...newSubscriber, email: e.target.value})}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium">Name (optional)</label>
                  <Input
                    id="name"
                    className="border-slate-200"
                    value={newSubscriber.name}
                    onChange={(e) => setNewSubscriber({...newSubscriber, name: e.target.value})}
                    placeholder="John Doe"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAddForm(false)} className="border-slate-200">Cancel</Button>
                <Button type="submit">Add Subscriber</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
      
      <div className="bg-white rounded-md border border-slate-200">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow className="hover:bg-slate-50">
              <TableHead className="w-[40px]">
                <input
                  type="checkbox"
                  checked={selectedSubscribers.length === subscribers.length && subscribers.length > 0}
                  onChange={handleToggleSelectAll}
                  className="h-4 w-4 rounded border-slate-300"
                />
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:text-slate-900"
                onClick={() => handleSort('email')}
              >
                Email 
                {sortBy === 'email' && (
                  sortOrder === 'asc' 
                    ? <ChevronUp className="h-4 w-4 inline ml-1" /> 
                    : <ChevronDown className="h-4 w-4 inline ml-1" />
                )}
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead 
                className="cursor-pointer hover:text-slate-900"
                onClick={() => handleSort('subscribed_at')}
              >
                Subscribed 
                {sortBy === 'subscribed_at' && (
                  sortOrder === 'asc' 
                    ? <ChevronUp className="h-4 w-4 inline ml-1" /> 
                    : <ChevronDown className="h-4 w-4 inline ml-1" />
                )}
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:text-slate-900"
                onClick={() => handleSort('last_activity_at')}
              >
                Last Activity 
                {sortBy === 'last_activity_at' && (
                  sortOrder === 'asc' 
                    ? <ChevronUp className="h-4 w-4 inline ml-1" /> 
                    : <ChevronDown className="h-4 w-4 inline ml-1" />
                )}
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array(5).fill(0).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-4 rounded" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                  <TableCell className="text-right"><Skeleton className="h-8 w-8 rounded-full ml-auto" /></TableCell>
                </TableRow>
              ))
            ) : subscribers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                  No subscribers found. Add one to get started.
                </TableCell>
              </TableRow>
            ) : (
              subscribers.map((subscriber) => (
                <TableRow key={subscriber.id} className="hover:bg-slate-50">
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedSubscribers.includes(subscriber.id)}
                      onChange={() => handleToggleSelectSubscriber(subscriber.id)}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                  </TableCell>
                  <TableCell className="font-medium">{subscriber.email}</TableCell>
                  <TableCell>{subscriber.name || "-"}</TableCell>
                  <TableCell>{getStatusBadge(subscriber.status)}</TableCell>
                  <TableCell>
                    {subscriber.subscribed_at 
                      ? format(new Date(subscriber.subscribed_at), 'MMM d, yyyy')
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {subscriber.last_activity_at
                      ? format(new Date(subscriber.last_activity_at), 'MMM d, yyyy')
                      : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => {
                            try {
                              const newStatus = subscriber.status === 'active' ? 'unsubscribed' : 'active';
                              supabase
                                .from('newsletter_subscriptions')
                                .update({ status: newStatus })
                                .eq('id', subscriber.id)
                                .then(({ error }) => {
                                  if (error) {
                                    console.error("Error updating subscriber status:", error);
                                    toast.error("Failed to update subscriber status");
                                    return;
                                  }
                                  
                                  toast.success(`Subscriber marked as ${newStatus}`);
                                  refetch();
                                });
                            } catch (error) {
                              console.error("Error updating subscriber status:", error);
                              toast.error("Failed to update subscriber status");
                            }
                          }}
                          disabled={!hasPermission('manage:newsletters')}
                        >
                          {subscriber.status === 'active' ? 'Mark as Unsubscribed' : 'Mark as Active'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(subscriber.id)}
                          disabled={!hasPermission('manage:newsletters')}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-slate-500">
          Showing {isLoading ? '...' : `${subscribers.length} of ${pagination.total}`} subscribers
        </div>
        <Pagination
          totalPages={pagination.totalPages}
          currentPage={page}
          onPageChange={setPage}
        />
      </div>
      
      {/* Bulk Action Confirmation Dialog */}
      <Dialog 
        open={bulkActionDialog.open} 
        onOpenChange={(open) => setBulkActionDialog({ ...bulkActionDialog, open })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Bulk Action</DialogTitle>
            <DialogDescription>
              {bulkActionDialog.action === 'delete' ? (
                `Are you sure you want to delete ${selectedSubscribers.length} subscribers? This action cannot be undone.`
              ) : bulkActionDialog.action === 'activate' ? (
                `Are you sure you want to mark ${selectedSubscribers.length} subscribers as active?`
              ) : (
                `Are you sure you want to mark ${selectedSubscribers.length} subscribers as unsubscribed?`
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              className="border-slate-200"
              onClick={() => setBulkActionDialog({ open: false, action: '' })}
            >
              Cancel
            </Button>
            <Button 
              variant={bulkActionDialog.action === 'delete' ? 'destructive' : 'default'}
              onClick={() => handleBulkAction(bulkActionDialog.action)}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
